import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import HotelIcon from '@mui/icons-material/Hotel';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authStore, logout } from '../api';

export function PublicHeader() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = authStore.get();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const close = () => setAnchor(null);
  const closeDrawer = () => setDrawerOpen(false);

  const onSignOut = async () => {
    close();
    closeDrawer();
    await logout();
    queryClient.clear();
    navigate('/');
  };

  const navLinks = [
    { label: 'Hotels', to: '/' },
    { label: 'Search', to: '/hotels' },
    ...(session ? [{ label: 'My bookings', to: '/my-bookings' }] : []),
  ];

  return (
    <AppBar position="sticky">
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ minHeight: 64, gap: 2 }}>
          {/* Logo */}
          <Stack
            component={Link}
            to="/"
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{ flexShrink: 0, textDecoration: 'none' }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.25,
                background: (t) =>
                  `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
              }}
            >
              <HotelIcon fontSize="small" />
            </Box>
            <Stack>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1, color: 'primary.main' }}>
                dmOTA
              </Typography>
              <Typography variant="caption" sx={{ lineHeight: 1, color: 'text.secondary' }}>
                Hotel booking
              </Typography>
            </Stack>
          </Stack>

          {/* Desktop nav links */}
          <Stack
            direction="row"
            spacing={2.5}
            sx={{ ml: 4, flex: 1, display: { xs: 'none', md: 'flex' } }}
          >
            {navLinks.map((l) => (
              <Button
                key={l.to + l.label}
                component={Link}
                to={l.to}
                sx={{ color: 'text.primary', fontWeight: 500 }}
              >
                {l.label}
              </Button>
            ))}
          </Stack>

          {/* Spacer on mobile so hamburger stays right */}
          <Box sx={{ flex: 1, display: { xs: 'flex', md: 'none' } }} />

          {/* Desktop auth buttons */}
          {!session ? (
            <Stack direction="row" spacing={1.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Button component={Link} to="/login" sx={{ color: 'primary.main', fontWeight: 600 }}>
                Sign in
              </Button>
              <Button component={Link} to="/signup" variant="contained" color="secondary">
                Sign up
              </Button>
            </Stack>
          ) : (
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <IconButton onClick={(e) => setAnchor(e.currentTarget)}>
                <AccountCircleIcon />
              </IconButton>
              <Menu open={!!anchor} anchorEl={anchor} onClose={close}>
                <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2">{session.user.fullName ?? 'Guest'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {session.user.email}
                  </Typography>
                </Box>
                <MenuItem onClick={() => { close(); navigate('/my-profile'); }}>My profile</MenuItem>
                <MenuItem onClick={() => { close(); navigate('/my-bookings'); }}>My bookings</MenuItem>
                <MenuItem onClick={onSignOut} sx={{ color: 'error.main' }}>Sign out</MenuItem>
              </Menu>
            </Box>
          )}

          {/* Mobile hamburger */}
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{ display: { xs: 'flex', md: 'none' } }}
            aria-label="Open menu"
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </Container>

      {/* Mobile navigation drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: 280 } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={closeDrawer} aria-label="Close menu">
            <CloseIcon />
          </IconButton>
        </Box>

        {session && (
          <Box sx={{ px: 2.5, pb: 2 }}>
            <Typography variant="subtitle2">{session.user.fullName ?? 'Guest'}</Typography>
            <Typography variant="caption" color="text.secondary">{session.user.email}</Typography>
          </Box>
        )}

        <Divider />

        <List disablePadding>
          {navLinks.map((l) => (
            <ListItemButton
              key={l.to + l.label}
              component={Link}
              to={l.to}
              onClick={closeDrawer}
            >
              <ListItemText primary={l.label} />
            </ListItemButton>
          ))}

          {session && (
            <ListItemButton component={Link} to="/my-profile" onClick={closeDrawer}>
              <ListItemText primary="My profile" />
            </ListItemButton>
          )}
        </List>

        <Divider />

        <Box sx={{ p: 2 }}>
          {!session ? (
            <Stack spacing={1.5}>
              <Button
                fullWidth
                variant="outlined"
                component={Link}
                to="/login"
                onClick={closeDrawer}
              >
                Sign in
              </Button>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                component={Link}
                to="/signup"
                onClick={closeDrawer}
              >
                Sign up
              </Button>
            </Stack>
          ) : (
            <Button fullWidth variant="outlined" color="error" onClick={onSignOut}>
              Sign out
            </Button>
          )}
        </Box>
      </Drawer>
    </AppBar>
  );
}
